import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ScrollText, Search, X, Download, RotateCw, AlertTriangle,
  FolderKanban, ShieldCheck, Database as DatabaseIcon,
} from 'lucide-react';
import api from '../lib/api';
import { fmtRelative } from '../lib/format';
import { cn } from '../lib/utils';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { EmptyState } from '../components/common';

const SOURCE_META = {
  project: { label: 'Costing / Project', tone: 'brand', icon: FolderKanban },
  access: { label: 'Access & Config', tone: 'teal', icon: ShieldCheck },
  master: { label: 'Rate Masters', tone: 'copper', icon: DatabaseIcon },
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const SourceBadge = ({ source }) => {
  const m = SOURCE_META[source] || SOURCE_META.access;
  const Icon = m.icon;
  return (
    <span data-testid={`audit-source-${source}`}
      className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium',
        `bg-[hsl(var(--${m.tone})/0.12)] text-[hsl(var(--${m.tone}))]`)}>
      <Icon size={11} />{m.label}
    </span>
  );
};

export default function AuditTrail() {
  const [q, setQ] = useState('');
  const [dq, setDq] = useState(''); // debounced search
  const [source, setSource] = useState('all');
  const [actor, setActor] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [limit, setLimit] = useState(100);

  const [data, setData] = useState(null);
  const [actors, setActors] = useState([]);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Debounce the free-text search.
  useEffect(() => { const t = setTimeout(() => setDq(q), 300); return () => clearTimeout(t); }, [q]);
  // Reset paging when any filter changes.
  useEffect(() => { setLimit(100); }, [dq, source, actor, from, to]);

  const params = useMemo(() => {
    const p = { limit };
    if (dq) p.q = dq;
    if (source !== 'all') p.source = source;
    if (actor !== 'all') p.actor = actor;
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [dq, source, actor, from, to, limit]);

  const load = useCallback(async () => {
    setError(false);
    try {
      const { data: d } = await api.get('/audit-trail', { params });
      setData(d);
      if (d.actors) setActors(d.actors);
    } catch (e) {
      setError(true);
      if (e?.response?.status === 403) setError('403');
    }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const activeFilters = source !== 'all' || actor !== 'all' || !!from || !!to || !!dq;
  const clearFilters = () => { setQ(''); setSource('all'); setActor('all'); setFrom(''); setTo(''); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data: d } = await api.get('/audit-trail', { params: { ...params, limit: 100000 } });
      const rows = d.events || [];
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const header = ['When', 'Actor', 'Source', 'Module', 'Type', 'Project', 'Event'];
      const lines = rows.map((e) => [
        fmtDateTime(e.at), e.actor, (SOURCE_META[e.source] || {}).label || e.source,
        e.module, e.type, e.projectCode || '', e.message,
      ].map(esc).join(','));
      const csv = [header.map(esc).join(','), ...lines].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} event(s)`);
    } catch {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  const events = data?.events || [];
  const total = data?.total || 0;

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 md:p-6 lg:p-8" data-testid="audit-trail-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold md:text-3xl">
            <ScrollText size={24} className="text-[hsl(var(--brand))]" />Audit Trail
          </h1>
          <p className="text-sm text-muted-foreground">
            Organisation-wide, read-only log of costing/project activity, access &amp; config changes and rate-master edits.
          </p>
        </div>
        <Button variant="outline" onClick={exportCsv} disabled={exporting || events.length === 0} data-testid="audit-export">
          <Download size={15} className="mr-2" />{exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>

      {/* Filters */}
      <div className="erak-card space-y-3 p-4" data-testid="audit-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search event, actor or project…" className="pl-9" />
          </div>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-[170px]" data-testid="audit-source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="project">Costing / Project</SelectItem>
              <SelectItem value="access">Access &amp; Config</SelectItem>
              <SelectItem value="master">Rate Masters</SelectItem>
            </SelectContent>
          </Select>
          <Select value={actor} onValueChange={setActor}>
            <SelectTrigger className="w-[170px]" data-testid="audit-actor-filter"><SelectValue placeholder="All users" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {actors.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Input type="date" data-testid="audit-from" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[150px] font-num" aria-label="From date" />
            <span>→</span>
            <Input type="date" data-testid="audit-to" value={to} onChange={(e) => setTo(e.target.value)} className="w-[150px] font-num" aria-label="To date" />
          </div>
          {activeFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="audit-clear-filters">
              <X size={14} className="mr-1.5" />Clear
            </Button>
          )}
        </div>
      </div>

      {/* Results */}
      {error === '403' ? (
        <EmptyState testid="audit-forbidden" icon={ShieldCheck} title="Admin only"
          description="The Audit Trail is available to Admin users only." />
      ) : error ? (
        <div className="erak-card flex flex-col items-center gap-3 p-10 text-center" data-testid="audit-error">
          <AlertTriangle size={26} className="text-[hsl(var(--danger))]" />
          <div className="text-sm text-muted-foreground">Could not load the audit trail.</div>
          <Button variant="outline" size="sm" onClick={load}><RotateCw size={14} className="mr-1.5" />Retry</Button>
        </div>
      ) : data === null ? (
        <div className="space-y-2" data-testid="audit-loading">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-muted/40" />)}
        </div>
      ) : events.length === 0 ? (
        <EmptyState testid="audit-empty" icon={ScrollText} title="No audit events"
          description={activeFilters ? 'No events match the current filters. Try clearing them.' : 'Activity across the system will appear here as it happens.'} />
      ) : (
        <>
          {/* Desktop table */}
          <div className="erak-card hidden overflow-x-auto md:block" data-testid="audit-table">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5 font-semibold">When</th>
                  <th className="px-4 py-2.5 font-semibold">Actor</th>
                  <th className="px-4 py-2.5 font-semibold">Source</th>
                  <th className="px-4 py-2.5 font-semibold">Event</th>
                  <th className="px-4 py-2.5 font-semibold">Project</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} data-testid={`audit-row-${e.id}`} className="border-b last:border-0 hover:bg-muted/20">
                    <td className="whitespace-nowrap px-4 py-2.5 align-top">
                      <div className="font-num text-xs tabular-nums">{fmtDateTime(e.at)}</div>
                      <div className="text-[10px] text-muted-foreground">{fmtRelative(e.at)}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 align-top font-medium">{e.actor || '—'}</td>
                    <td className="px-4 py-2.5 align-top"><SourceBadge source={e.source} /></td>
                    <td className="px-4 py-2.5 align-top">{e.message}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 align-top font-num text-xs text-[hsl(var(--copper))]">{e.projectCode || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-2 md:hidden" data-testid="audit-cards">
            {events.map((e) => (
              <div key={e.id} className="erak-card p-3" data-testid={`audit-card-${e.id}`}>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <SourceBadge source={e.source} />
                  <span className="font-num text-[10px] tabular-nums text-muted-foreground">{fmtDateTime(e.at)}</span>
                </div>
                <div className="text-sm">{e.message}</div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="font-medium text-foreground">{e.actor || '—'}</span>
                  {e.projectCode && <span className="font-num text-[hsl(var(--copper))]">· {e.projectCode}</span>}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 pt-1">
            <span className="text-xs text-muted-foreground" data-testid="audit-count">
              Showing {events.length} of {total} event{total === 1 ? '' : 's'}
            </span>
            {events.length < total && (
              <Button variant="outline" size="sm" data-testid="audit-load-more" onClick={() => setLimit((l) => l + 100)}>
                Load more
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
